import { useEffect, useState } from 'react'
import { api } from '../api/client.ts'
import { Avatar } from '../components/Avatar.tsx'
import { MedicationSheet, type MedicationDraft } from '../components/MedicationSheet.tsx'
import {
  HealthRecordSheet,
  RECORD_KINDS,
  type RecordDraft,
} from '../components/HealthRecordSheet.tsx'
import { SharePanel } from '../components/SharePanel.tsx'
import { Icon } from '../components/Icon.tsx'
import { useAuth } from '../store/auth.ts'
import { clockTime } from '../lib/planner.ts'

/**
 * ДНЕВНИК ЗДОРОВЬЯ
 *
 * Раздел открывается на «сегодня»: что принять, к кому идти, какие были
 * последние измерения. Историю листают редко, а таблетку принимают каждый
 * день — значит, она и должна быть первой.
 *
 * Чужой дневник, если его открыли, показывается только для чтения.
 * Отметить приём за другого нельзя: это была бы запись о том, чего
 * не было.
 */

type Dose = {
  medicationId: string
  name: string
  dosage: string | null
  scheduledAt: string
  takenAt: string | null
  skipped: boolean
}

type Metric = {
  id?: string
  kind: string
  value: number
  valueTwo: number | null
  unit: string | null
  recordedAt: string
}

type Person = { id: string; displayName: string; avatarUrl: string | null; mine?: boolean }

type Medication = MedicationDraft & { id: string; finished: boolean }

const METRIC_NAMES: Record<string, string> = {
  STEPS: 'Шаги',
  HEART_RATE: 'Пульс',
  BLOOD_PRESSURE: 'Давление',
  WEIGHT: 'Вес',
  TEMPERATURE: 'Температура',
  SLEEP_MINUTES: 'Сон',
  BLOOD_SUGAR: 'Сахар',
  SPO2: 'Кислород',
}

const METRIC_UNITS: Record<string, string> = {
  STEPS: '',
  HEART_RATE: 'уд/мин',
  BLOOD_PRESSURE: '',
  WEIGHT: 'кг',
  TEMPERATURE: '°',
  SLEEP_MINUTES: 'мин',
  BLOOD_SUGAR: 'ммоль/л',
  SPO2: '%',
}

/** «120/80», «72 уд/мин», «8 431» — каждое измерение читается по-своему. */
function metricText(metric: Metric): string {
  if (metric.kind === 'BLOOD_PRESSURE') return `${metric.value}/${metric.valueTwo ?? '—'}`
  if (metric.kind === 'SLEEP_MINUTES') {
    return `${Math.floor(metric.value / 60)} ч ${Math.round(metric.value % 60)} мин`
  }
  const unit = metric.unit ?? METRIC_UNITS[metric.kind] ?? ''
  return `${metric.value.toLocaleString('ru-RU')}${unit ? ' ' + unit : ''}`
}

export function HealthPage() {
  const me = useAuth((s) => s.user)

  const [people, setPeople] = useState<Person[]>([])
  const [whoseId, setWhoseId] = useState<string | null>(null)
  const [tab, setTab] = useState<'today' | 'pills' | 'records' | 'metrics'>('today')
  const [sharing, setSharing] = useState(false)

  /**
   * Что добавляет плюс в шапке, зависит от вкладки.
   *
   * Раньше добавление жило кнопкой в теле раздела, а во всех остальных
   * разделах — плюсом в шапке. Разница мелкая, но именно из таких
   * мелочей и складывается ощущение, что приложение собрано из кусков.
   */
  const [adding, setAdding] = useState(0)

  useEffect(() => {
    api<{ people: Person[] }>('/api/health/whose')
      .then((data) => setPeople(data.people))
      .catch(() => setPeople([]))
  }, [])

  const target = whoseId ?? me?.id ?? null
  const mine = target === me?.id
  const suffix = mine ? '' : `&userId=${target}`
  const query = mine ? '' : `?userId=${target}`

  return (
    <div className="section">
      <header className="section-head">
        <div className="section-title">
          <h1>Здоровье</h1>
          {!mine && (
            <span className="section-sub">
              дневник {people.find((p) => p.id === target)?.displayName}
            </span>
          )}
        </div>

        {mine && (
          <button className="icon-btn" onClick={() => setSharing(true)} title="Кому открыт дневник">
            <Icon name="family" size={21} />
          </button>
        )}

        {mine && (tab === 'pills' || tab === 'records') && (
          <button
            className="icon-btn"
            onClick={() => setAdding((n) => n + 1)}
            aria-label={tab === 'pills' ? 'Добавить лекарство' : 'Добавить запись'}
          >
            <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
              <path d="M11 11V5h2v6h6v2h-6v6h-2v-6H5v-2z" />
            </svg>
          </button>
        )}
      </header>

      {people.length > 1 && (
        <div className="whose-row">
          {people.map((person) => (
            <button
              key={person.id}
              className={'person-chip' + (person.id === target ? ' is-picked' : '')}
              onClick={() => setWhoseId(person.id)}
            >
              <Avatar
                id={person.id}
                name={person.displayName}
                url={person.avatarUrl}
                size={22}
              />
              <span>{person.mine ? 'Я' : person.displayName}</span>
            </button>
          ))}
        </div>
      )}

      <div className="planner-tabs">
        {(
          [
            ['today', 'Сегодня'],
            ['pills', 'Лекарства'],
            ['records', 'Записи'],
            ['metrics', 'Показатели'],
          ] as const
        ).map(([id, name]) => (
          <button
            key={id}
            className={'tab-btn' + (tab === id ? ' is-active' : '')}
            onClick={() => setTab(id)}
          >
            {name}
          </button>
        ))}
      </div>

      {tab === 'today' && <TodayView query={query} mine={mine} />}
      {tab === 'pills' && <PillsView query={query} mine={mine} addWhen={adding} />}
      {tab === 'records' && <RecordsView query={query} mine={mine} addWhen={adding} />}
      {tab === 'metrics' && <MetricsView query={query} suffix={suffix} mine={mine} />}

      {sharing && me && (
        <SharePanel
          entityType="HEALTH_DIARY"
          entityId={me.id}
          onClose={() => setSharing(false)}
          title="Кому открыт мой дневник здоровья"
        />
      )}
    </div>
  )
}

/* Сегодня ------------------------------------------------------------------ */

function TodayView({ query, mine }: { query: string; mine: boolean }) {
  const [data, setData] = useState<{
    doses: Dose[]
    appointments: { id: string; title: string; startsAt: string; location: string | null }[]
    latest: Metric[]
  } | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = () => {
    api<typeof data>(`/api/health/today${query}`)
      .then(setData)
      .catch((e) => setError(e instanceof Error ? e.message : 'Дневник закрыт'))
  }

  useEffect(load, [query])

  const mark = async (dose: Dose, taken: boolean) => {
    await api('/api/health/intakes', {
      method: 'POST',
      body: { medicationId: dose.medicationId, scheduledAt: dose.scheduledAt, taken },
    })
    load()
  }

  if (error) {
    return (
      <div className="section-body">
        <p className="empty-hint">{error}</p>
      </div>
    )
  }

  if (!data) {
    return (
      <div className="section-body">
        <div className="center-screen" style={{ height: 80 }}>
          <div className="spinner" />
        </div>
      </div>
    )
  }

  return (
    <div className="section-body scroll">
      <h3 className="sheet-sub">Приём лекарств сегодня</h3>
      {data.doses.length === 0 && <p className="comments-empty">На сегодня ничего не назначено</p>}

      {data.doses.map((dose) => (
        <div
          key={dose.medicationId + dose.scheduledAt}
          className={'item-row' + (dose.takenAt ? ' is-done' : '')}
        >
          <button
            className="item-check"
            onClick={() => void mark(dose, !dose.takenAt)}
            disabled={!mine}
            aria-label={dose.takenAt ? 'Отменить отметку' : 'Принято'}
          >
            {dose.takenAt ? '✓' : ''}
          </button>

          <span className="item-main" style={{ cursor: 'default' }}>
            <span className="item-title">
              {dose.name}
              {dose.dosage && <span className="item-amount">{dose.dosage}</span>}
            </span>
            <span className="item-marks">
              <span>{clockTime(dose.scheduledAt)}</span>
            </span>
          </span>
        </div>
      ))}

      {data.appointments.length > 0 && (
        <>
          <h3 className="sheet-sub">Ближайшие визиты</h3>
          {data.appointments.map((visit) => (
            <div key={visit.id} className="event-row">
              <span className="event-time">
                {new Date(visit.startsAt).toLocaleDateString('ru-RU', {
                  day: 'numeric',
                  month: 'short',
                })}
              </span>
              <span className="event-main" style={{ cursor: 'default' }}>
                <span className="event-bar" style={{ background: 'var(--accent)' }} />
                <span className="event-text">
                  <span className="event-title">🩺 {visit.title}</span>
                  <span className="event-sub">
                    <span>{clockTime(visit.startsAt)}</span>
                    {visit.location && <span>{visit.location}</span>}
                  </span>
                </span>
              </span>
            </div>
          ))}
        </>
      )}

      <h3 className="sheet-sub">Последние измерения</h3>
      {data.latest.length === 0 && (
        <p className="comments-empty">Пока ничего не записано</p>
      )}

      <div className="metric-cards">
        {data.latest.map((metric) => (
          <div key={metric.kind} className="metric-card">
            <span className="metric-name">{METRIC_NAMES[metric.kind] ?? metric.kind}</span>
            <span className="metric-value">{metricText(metric)}</span>
            <span className="metric-when">
              {new Date(metric.recordedAt).toLocaleDateString('ru-RU')}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

/* Лекарства ---------------------------------------------------------------- */

function PillsView({
  query,
  mine,
  addWhen,
}: {
  query: string
  mine: boolean
  /** Счётчик нажатий на плюс в шапке: растёт — открываем пустой лист */
  addWhen: number
}) {
  const [medications, setMedications] = useState<Medication[] | null>(null)
  const [draft, setDraft] = useState<MedicationDraft | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = () => {
    api<{ medications: Medication[] }>(`/api/health/medications${query}`)
      .then((data) => setMedications(data.medications))
      .catch((e) => setError(e instanceof Error ? e.message : 'Дневник закрыт'))
  }

  useEffect(load, [query])

  useEffect(() => {
    if (addWhen > 0) setDraft({ name: '' })
  }, [addWhen])

  if (error) {
    return (
      <div className="section-body">
        <p className="empty-hint">{error}</p>
      </div>
    )
  }

  return (
    <div className="section-body scroll">
      {medications?.length === 0 && (
        <p className="empty-hint">
          {mine
            ? 'Лекарств пока нет. Добавь первое плюсом в шапке — и приложение будет напоминать о приёме.'
            : 'Лекарств пока нет.'}
        </p>
      )}

      {medications?.map((medication) => (
        <button
          key={medication.id}
          className={'pill-row' + (medication.finished ? ' is-finished' : '')}
          onClick={() => mine && setDraft(medication)}
        >
          <span className="pill-name">
            {medication.name}
            {medication.dosage && <span className="item-amount">{medication.dosage}</span>}
          </span>

          <span className="pill-when">
            {medication.rrule
              ? (medication.times ?? []).join(', ') || 'без времени'
              : 'по необходимости'}
            {medication.finished && ' · курс окончен'}
          </span>
        </button>
      ))}

      {draft && (
        <MedicationSheet draft={draft} onClose={() => setDraft(null)} onSaved={load} />
      )}
    </div>
  )
}

/* Записи ------------------------------------------------------------------- */

function RecordsView({
  query,
  mine,
  addWhen,
}: {
  query: string
  mine: boolean
  addWhen: number
}) {
  const [records, setRecords] = useState<(RecordDraft & { id: string })[] | null>(null)
  const [draft, setDraft] = useState<RecordDraft | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = () => {
    api<{ records: (RecordDraft & { id: string })[] }>(`/api/health/records${query}`)
      .then((data) => setRecords(data.records))
      .catch((e) => setError(e instanceof Error ? e.message : 'Дневник закрыт'))
  }

  useEffect(load, [query])

  useEffect(() => {
    if (addWhen > 0) {
      setDraft({
        kind: 'CONCLUSION',
        title: '',
        recordedOn: new Date().toISOString().slice(0, 10),
      })
    }
  }, [addWhen])

  if (error) {
    return (
      <div className="section-body">
        <p className="empty-hint">{error}</p>
      </div>
    )
  }

  return (
    <div className="section-body scroll">
      {records?.length === 0 && (
        <p className="empty-hint">
          Записей нет. Сюда складывают заключения врачей, анализы и снимки —
          {mine ? ' добавь первую плюсом в шапке.' : ' пока пусто.'}
        </p>
      )}

      {records?.map((record) => (
        <button key={record.id} className="record-row" onClick={() => setDraft(record)}>
          <span className="record-date">
            {new Date(record.recordedOn).toLocaleDateString('ru-RU')}
          </span>

          <span className="record-text">
            <span className="record-title">{record.title}</span>
            <span className="record-sub">
              <span>{RECORD_KINDS[record.kind] ?? record.kind}</span>
              {record.doctorName && <span>{record.doctorName}</span>}
              {(record.files?.length ?? 0) > 0 && <span>📎 {record.files?.length}</span>}
            </span>
          </span>
        </button>
      ))}

      {draft && (
        <HealthRecordSheet draft={draft} onClose={() => setDraft(null)} onSaved={load} />
      )}
    </div>
  )
}

/* Показатели --------------------------------------------------------------- */

function MetricsView({
  query,
  suffix,
  mine,
}: {
  query: string
  suffix: string
  mine: boolean
}) {
  const [kind, setKind] = useState('BLOOD_PRESSURE')
  const [metrics, setMetrics] = useState<Metric[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  const [value, setValue] = useState('')
  const [valueTwo, setValueTwo] = useState('')
  const [busy, setBusy] = useState(false)

  const load = () => {
    api<{ metrics: Metric[] }>(`/api/health/metrics?kind=${kind}${suffix}`)
      .then((data) => setMetrics(data.metrics))
      .catch((e) => setError(e instanceof Error ? e.message : 'Дневник закрыт'))
  }

  useEffect(load, [kind, query])

  const add = async () => {
    const main = Number(value.replace(',', '.'))
    if (!Number.isFinite(main) || main <= 0) return

    setBusy(true)
    try {
      await api('/api/health/metrics', {
        method: 'POST',
        body: {
          kind,
          value: main,
          valueTwo: kind === 'BLOOD_PRESSURE' ? Number(valueTwo.replace(',', '.')) || null : null,
          recordedAt: new Date().toISOString(),
        },
      })
      setValue('')
      setValueTwo('')
      load()
    } finally {
      setBusy(false)
    }
  }

  if (error) {
    return (
      <div className="section-body">
        <p className="empty-hint">{error}</p>
      </div>
    )
  }

  return (
    <div className="section-body scroll">
      <label className="field">
        <span className="field-label">Что смотрим</span>
        <select className="input" value={kind} onChange={(e) => setKind(e.target.value)}>
          {Object.entries(METRIC_NAMES).map(([id, name]) => (
            <option key={id} value={id}>
              {name}
            </option>
          ))}
        </select>
      </label>

      {mine && (
        <div className="add-row" style={{ border: 'none', padding: '8px 0' }}>
          <input
            className="input"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={kind === 'BLOOD_PRESSURE' ? 'Верхнее' : 'Значение'}
            inputMode="decimal"
          />
          {kind === 'BLOOD_PRESSURE' && (
            <input
              className="input"
              value={valueTwo}
              onChange={(e) => setValueTwo(e.target.value)}
              placeholder="Нижнее"
              inputMode="decimal"
            />
          )}
          <button className="btn-primary" onClick={() => void add()} disabled={busy || !value}>
            Записать
          </button>
        </div>
      )}

      {metrics && metrics.length > 1 && <MetricChart metrics={metrics} />}

      {metrics?.length === 0 && <p className="empty-hint">Измерений пока нет.</p>}

      {metrics?.slice(0, 60).map((metric) => (
        <div key={metric.id ?? metric.recordedAt} className="metric-row">
          <span className="metric-row-value">{metricText(metric)}</span>
          <span className="metric-row-when">
            {new Date(metric.recordedAt).toLocaleString('ru-RU', {
              day: 'numeric',
              month: 'short',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
        </div>
      ))}
    </div>
  )
}

/**
 * Простая линия по последним измерениям.
 *
 * Без подписей осей и сетки намеренно: здесь важно «растёт или падает»,
 * а точные числа лежат списком ниже. Рисуем сами, без библиотеки —
 * ради одной линии тянуть стороннюю зависимость незачем.
 */
function MetricChart({ metrics }: { metrics: Metric[] }) {
  const points = [...metrics].reverse().slice(-30)
  const values = points.map((m) => m.value)
  const min = Math.min(...values)
  const max = Math.max(...values)
  const span = max - min || 1

  const width = 300
  const height = 70

  const line = points
    .map((metric, index) => {
      const x = (index / Math.max(points.length - 1, 1)) * width
      const y = height - ((metric.value - min) / span) * (height - 10) - 5
      return `${index === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`
    })
    .join(' ')

  return (
    <div className="metric-chart">
      <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" aria-hidden="true">
        <path d={line} fill="none" stroke="var(--accent)" strokeWidth="2" />
      </svg>
      <div className="metric-chart-bounds">
        <span>{min.toLocaleString('ru-RU')}</span>
        <span>{max.toLocaleString('ru-RU')}</span>
      </div>
    </div>
  )
}
